import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { HardHat, Pencil, Plus, Trash2 } from "lucide-react";

const EMPTY = { full_name: "", contact: "", whatsapp: "", email: "", pincodes: "", state: "", district: "", taluk: "", username: "", password: "" };

const F = [
  { key: "full_name", label: "FE Full Name", required: true },
  { key: "contact", label: "FE Contact Number" },
  { key: "whatsapp", label: "FE WhatsApp No" },
  { key: "email", label: "FE Email ID", type: "email" },
  { key: "state", label: "FE State" },
  { key: "district", label: "FE District" },
  { key: "taluk", label: "FE Taluk" },
  { key: "username", label: "FE Username (login email)" },
  { key: "password", label: "FE Password", type: "password" },
];

export default function FieldExecutives() {
  const [fes, setFes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/field-executives").then((r) => setFes(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (f) => {
    setEditing(f);
    setForm({ ...EMPTY, ...f, pincodes: (f.pincodes || []).join(", "), password: "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, pincodes: form.pincodes.split(",").map((x) => x.trim()).filter(Boolean) };
    if (!payload.password) delete payload.password;
    if (!payload.username) delete payload.username;
    try {
      if (editing) await api.patch(`/field-executives/${editing.id}`, payload);
      else await api.post("/field-executives", payload);
      toast.success(editing ? "Field Executive updated" : "Field Executive created");
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete FE "${f.full_name}"? Their login account will be deactivated.`)) return;
    try {
      await api.delete(`/field-executives/${f.id}`);
      toast.success("Field Executive deleted");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-4" data-testid="field-executives-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-[#111827] tracking-tight">Field Executives</h1>
          <p className="text-sm text-[#4B5563] mt-1">FE master records with coverage areas. Adding a username + password creates an FE login.</p>
        </div>
        <Button onClick={openCreate} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-fe-button">
          <Plus className="h-4 w-4 mr-2" /> Add Field Executive
        </Button>
      </div>

      <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="fes-table">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              {["FE Name", "Contact", "WhatsApp", "Email", "Pincodes", "State", "District", "Taluk", "Login", "Actions"].map((h) => (
                <th key={h} className="text-left text-[10px] font-bold uppercase tracking-widest text-[#4B5563] py-2 px-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fes.length === 0 && (
              <tr><td colSpan={10} className="py-10 text-center text-[#9CA3AF]" data-testid="fes-empty">No field executives yet. Add your first FE.</td></tr>
            )}
            {fes.map((f) => (
              <tr key={f.id} className="border-b border-[#F3F4F6] hover:bg-[#F3F4F6] transition-colors" data-testid={`fe-row-${f.id}`}>
                <td className="py-2 px-3 font-medium flex items-center gap-2"><HardHat className="h-4 w-4 text-[#9CA3AF]" />{f.full_name}</td>
                <td className="py-2 px-3 font-mono text-xs">{f.contact || "—"}</td>
                <td className="py-2 px-3 font-mono text-xs">{f.whatsapp || "—"}</td>
                <td className="py-2 px-3 text-xs">{f.email || "—"}</td>
                <td className="py-2 px-3 font-mono text-xs max-w-[180px] truncate" title={(f.pincodes || []).join(", ")}>{(f.pincodes || []).length ? f.pincodes.join(", ") : "—"}</td>
                <td className="py-2 px-3">{f.state || "—"}</td>
                <td className="py-2 px-3">{f.district || "—"}</td>
                <td className="py-2 px-3">{f.taluk || "—"}</td>
                <td className="py-2 px-3 text-xs">{f.username || "—"}</td>
                <td className="py-2 px-3">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(f)} className="text-[#9CA3AF] hover:text-[#2563EB] transition-colors" data-testid={`edit-fe-${f.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(f)} className="text-[#9CA3AF] hover:text-[#EF4444] transition-colors" data-testid={`delete-fe-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="fe-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading font-extrabold">{editing ? `Edit — ${editing.full_name}` : "Add Field Executive"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {F.map((f) => (
              <div key={f.key}>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">{f.label}{f.required && " *"}</Label>
                <Input type={f.type || "text"} required={!!f.required} value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1 rounded-sm" data-testid={`fe-form-${f.key}`} />
              </div>
            ))}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">FE Pincodes (comma separated)</Label>
              <Input value={form.pincodes} onChange={(e) => setForm({ ...form, pincodes: e.target.value })}
                className="mt-1 rounded-sm" placeholder="411001, 411045, 560001" data-testid="fe-form-pincodes" />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" className="w-full rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="fe-save-button">
                {editing ? "Save Changes" : "Create Field Executive"}
              </Button>
              {editing && <p className="text-[10px] text-[#9CA3AF] mt-2">Leave password blank to keep the existing one. Entering a new password updates the FE login.</p>}
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
